import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  GOOGLE_AUTH_OUTCOMES,
  signInWithGoogle,
  signOutUser,
} from "../../../utils/auth/googleAuth";
import AccountLinkingSheet from "./AccountLinkingSheet";
import { getYourImpact } from "../../../utils/empathyAI/getYourImpact";
import {
  clearAccountMenuIntent,
  getAccountSessionRecoveryState,
  hasUnfinishedAccountSessionRecovery,
  saveAccountMenuIntent,
} from "../../../utils/auth/accountSessionRecovery";

function getCleanString(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function getAccountEmail(user) {
  return getCleanString(user?.email);
}

function getAccountDisplayName(user) {
  if (user?.isAnonymous === true) {
    return "Guest session";
  }

  if (!user) {
    return "LettuceTalk account";
  }

  return (
    getCleanString(user?.displayName) || getAccountEmail(user) || "Your account"
  );
}

function getAccountInitials(user) {
  const displayName = getCleanString(user?.displayName);
  const email = getAccountEmail(user);
  const source = displayName || email.split("@")[0] || "";

  if (!source) {
    return "";
  }

  const parts = source
    .split(/[\s._-]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const initials = (parts.length > 1 ? parts.slice(0, 2) : parts)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return initials || source.charAt(0).toUpperCase();
}

function AccountFallbackIcon({ className = "h-5 w-5" }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

function AccountAvatar({
  user,
  className = "h-10 w-10",
  textClassName = "text-sm",
  showBorder = true,
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const photoUrl = getCleanString(user?.photoURL);
  const initials = getAccountInitials(user);

  useEffect(() => {
    setImageFailed(false);
  }, [photoUrl]);

  return (
    <span
      className={`flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#f4d94e]/10 font-black text-[#fff6b8] shadow-[0_12px_34px_rgba(0,0,0,0.24)] ${showBorder ? "border border-[#f4d94e]/28" : ""} ${className}`}
      aria-hidden="true"
    >
      {photoUrl && !imageFailed ? (
        <img
          src={photoUrl}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : initials ? (
        <span className={textClassName}>{initials}</span>
      ) : (
        <AccountFallbackIcon className="h-5 w-5 text-[#fff6b8]" />
      )}
    </span>
  );
}

function AccountMenuRow({
  title,
  helper,
  to,
  href,
  onClick,
  disabled = false,
  compact = false,
  children,
}) {
  const Component = to ? Link : href ? "a" : "button";
  const componentProps = {
    className: `flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.045] text-left text-white/78 no-underline transition hover:border-[#f4d94e]/20 hover:bg-white/[0.07] hover:text-white visited:text-white/78 disabled:cursor-default disabled:opacity-60 disabled:hover:border-white/10 disabled:hover:bg-white/[0.045] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f4d94e] ${compact ? "px-3 py-2" : "px-4 py-3"}`,
    onClick,
  };

  if (to) {
    componentProps.to = to;
  } else if (href) {
    componentProps.href = href;
    componentProps.target = "_blank";
    componentProps.rel = "noreferrer";
  } else {
    componentProps.type = "button";
    componentProps.disabled = disabled;
  }

  return (
    <Component {...componentProps}>
      <span className={`flex shrink-0 items-center justify-center rounded-full border border-white/10 bg-black/16 text-white/78 ${compact ? "h-8 w-8" : "h-10 w-10"}`}>
        {children}
      </span>
      <span className="min-w-0">
        <span className={`block truncate text-sm text-white ${compact ? "font-bold" : "font-black"}`}>
          {title}
        </span>
        {helper ? (
          <span className="mt-0.5 block truncate text-xs font-bold text-white/52">
            {helper}
          </span>
        ) : null}
      </span>
    </Component>
  );
}

function CelebratoryImpactMark() {
  return (
    <svg
      aria-hidden="true"
      className="h-14 w-14 shrink-0 text-[#f4d94e] sm:h-16 sm:w-16"
      viewBox="0 0 80 80"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.4"
    >
      <path d="M40 49V27" />
      <path d="M40 34c-10-1-16-7-16-16 10 0 16 6 16 16Z" fill="currentColor" opacity="0.9" stroke="none" />
      <path d="M40 30c10-1 16-7 16-16-10 0-16 6-16 16Z" fill="currentColor" opacity="0.9" stroke="none" />
      <path d="M26 50c-8 2-13 6-13 10 0 7 12 11 27 11s27-4 27-11c0-4-5-8-13-10" />
      <path d="M20 55c-5 2-8 5-8 8 0 7 12 12 28 12s28-5 28-12c0-3-3-6-8-8" opacity="0.8" />
    </svg>
  );
}

const impactPanelClassName =
  "rounded-[1.35rem] border border-[#f4d94e]/70 bg-[#17170f] px-4 py-3.5 shadow-[0_0_0_1px_rgba(244,217,78,0.12),0_0_24px_rgba(244,217,78,0.14),inset_0_1px_0_rgba(255,246,184,0.12)] sm:px-5";

function ImpactLoadingShell() {
  return (
    <div className={`${impactPanelClassName} min-h-[7.5rem]`} role="status" aria-label="Measuring your impact">
      <div className="flex min-h-[4.5rem] items-center gap-3 sm:gap-4">
        <CelebratoryImpactMark />
        <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-white/68">
          <span
            aria-hidden="true"
            className="motion-safe:animate-spin motion-reduce:opacity-70 h-4 w-4 shrink-0 rounded-full border-2 border-[#f4d94e]/25 border-t-[#f4d94e]"
          />
          <span>Measuring your impact</span>
        </div>
      </div>
    </div>
  );
}

function YourImpactPanel({ impact, isLoading, errorMessage, onRetry, isAnonymous }) {
  if (isLoading) {
    return <ImpactLoadingShell />;
  }

  if (errorMessage) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.045] px-4 py-3">
        <p className="text-sm font-black text-white">Your Impact</p>
        <p className="mt-1 text-xs font-bold leading-5 text-white/58">{errorMessage}</p>
        <button
          type="button"
          className="mt-3 rounded-full border border-[#f4d94e]/30 px-3 py-1.5 text-xs font-black text-[#fff6b8] transition hover:bg-[#f4d94e]/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#f4d94e]"
          onClick={onRetry}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!impact || impact.totalImpact < 1) {
    return (
      <section
        aria-labelledby="your-impact-title"
        className={impactPanelClassName}
      >
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          <CelebratoryImpactMark />
          <div className="min-w-0">
            <p id="your-impact-title" className="text-xs text-[#fff6b8]">
              Your Impact
            </p>
            <p className="mt-1 break-words text-sm font-black leading-tight text-white sm:text-lg">
              Share your first card to start your impact
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="your-impact-title"
      className={`${impactPanelClassName} min-h-[7.5rem]`}
    >
      <div className="flex min-w-0 items-center gap-3 sm:gap-4">
        <CelebratoryImpactMark />
        <div className="min-w-0">
          <p id="your-impact-title" className="text-xs text-[#fff6b8]">
            Your Impact
          </p>
          <p className="mt-1 break-words text-sm font-black leading-tight text-white sm:text-lg">
            You helped open {impact.totalImpact} conversation{impact.totalImpact === 1 ? "" : "s"}
          </p>
          <p className="mt-1 break-words text-xs leading-5 text-white/68">
            {impact.startedByUser > 0 ? (
              <span>{impact.startedByUser} started by you</span>
            ) : null}
            {impact.startedByUser > 0 && impact.carriedForward > 0 ? (
              <span aria-hidden="true"> · </span>
            ) : null}
            {impact.carriedForward > 0 ? (
              <span>{impact.carriedForward} carried forward</span>
            ) : null}
          </p>
          {isAnonymous ? (
            <p className="mt-2 text-xs leading-5 text-white/58">
              Sign in to keep your impact across devices.
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function AccountMenu({
  user,
  statusMessage,
  onDismiss,
  onSignIn,
  onSignOut,
  impact,
  isImpactLoading,
  impactError,
  onRetryImpact,
}) {
  const isSignedInAccount = Boolean(user && user.isAnonymous !== true);
  const isAnonymousUser = user?.isAnonymous === true;
  const displayName = getAccountDisplayName(user);
  const email = getAccountEmail(user);
  const accountHelper = isSignedInAccount
    ? email || "LettuceTalk account"
    : isAnonymousUser
      ? "Create an account to save cards and make more."
      : "Sign in to save cards and return later.";
  const showFeedback =
    typeof window !== "undefined" &&
    window.location.hostname === "beta.lettucetalk.pages.dev";

  const menu = (
    <div
      className="fixed inset-0 z-[1000] flex items-end justify-center bg-black/64 px-4 pb-4 pt-10 backdrop-blur-sm sm:items-center sm:pb-10"
      onClick={onDismiss}
      data-testid="account-menu-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="empathy-account-menu-title"
        className="max-h-[calc(100dvh-3.5rem)] w-full max-w-md overflow-y-auto overscroll-contain animate-in fade-in slide-in-from-bottom-4 rounded-[1.4rem] border border-white/14 bg-[#14170f] p-5 text-left text-white shadow-[0_28px_90px_rgba(0,0,0,0.52)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="mx-auto mb-5 h-1.5 w-12 rounded-full bg-white/24"
          aria-hidden="true"
        />

        <div className="flex min-w-0 items-center gap-4">
          <AccountAvatar
            user={user}
            className="h-14 w-14"
            textClassName="text-lg"
          />
          <div className="min-w-0">
            <h2
              id="empathy-account-menu-title"
              className="truncate text-base font-black leading-5 text-white"
            >
              {displayName}
            </h2>
            {email ? (
              <p className="mt-1 truncate text-sm font-semibold text-white/58">
                {email}
              </p>
            ) : (
              <p className="mt-1 truncate text-sm font-semibold text-white/58">
                {accountHelper}
              </p>
            )}
          </div>
        </div>

        {statusMessage ? (
          <p
            role="status"
            className="mt-5 rounded-2xl border border-[#f4d94e]/18 bg-[#f4d94e]/10 px-4 py-3 text-sm font-bold leading-5 text-[#fff6b8]"
          >
            {statusMessage}
          </p>
        ) : null}

        {isSignedInAccount ? (
          <div className="mt-5">
            <YourImpactPanel
              impact={impact}
              isLoading={isImpactLoading}
              errorMessage={impactError}
              onRetry={onRetryImpact}
              isAnonymous={isAnonymousUser}
            />
          </div>
        ) : null}

        <div className="mt-5 space-y-3">
          {isSignedInAccount ? (
            <>
              <AccountMenuRow
                title="Create Card"
                helper="Start a gentle question"
                to="/refine"
                onClick={onDismiss}
              >
                <svg
                  aria-hidden="true"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
              </AccountMenuRow>
              <AccountMenuRow
                title="My Cards"
                helper="View your saved cards"
                to="/your-cards"
                onClick={onDismiss}
              >
                <svg
                  aria-hidden="true"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="4" y="4" width="7" height="7" rx="1.5" />
                  <rect x="13" y="4" width="7" height="7" rx="1.5" />
                  <rect x="4" y="13" width="7" height="7" rx="1.5" />
                  <rect x="13" y="13" width="7" height="7" rx="1.5" />
                </svg>
              </AccountMenuRow>
            </>
          ) : (
            <AccountMenuRow
              title="Sign in with Google"
              helper={
                isAnonymousUser
                  ? "Save cards from this session"
                  : "Create or open your account"
              }
              onClick={onSignIn}
            >
              <AccountFallbackIcon className="h-5 w-5" />
            </AccountMenuRow>
          )}

          {showFeedback ? (
            <AccountMenuRow
              title="Give Feedback"
              helper="Help us make LettuceTalk better"
              href="https://lettucetalk.canny.io/lettucetalk"
              onClick={onDismiss}
            >
              <svg
                aria-hidden="true"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 11.5a7.5 7.5 0 0 1-7.5 7.5H8l-4 2v-5.2A7.5 7.5 0 1 1 20 11.5Z" />
                <path d="M8.5 11.5h.01M12 11.5h.01M15.5 11.5h.01" />
              </svg>
            </AccountMenuRow>
          ) : null}

          {isSignedInAccount ? (
            <AccountMenuRow
              title="Sign out"
              helper="Sign out of this account"
              onClick={onSignOut}
            >
              <svg
                aria-hidden="true"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10 17 15 12 10 7" />
                <path d="M15 12H3" />
                <path d="M14 4h4a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3h-4" />
              </svg>
            </AccountMenuRow>
          ) : null}
        </div>

        <nav aria-label="About LettuceTalk" className="mt-4 border-t border-white/10 pt-4">
          <AccountMenuRow
            compact
            title="About LettuceTalk"
            href="https://lettucetalk.im"
            onClick={onDismiss}
          >
            <svg aria-hidden="true" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 11v5M12 8h.01" />
            </svg>
          </AccountMenuRow>
        </nav>
      </div>
    </div>
  );

  return typeof document === "undefined"
    ? menu
    : createPortal(menu, document.body);
}

function AccountAvatarMenu({
  user,
  signOutRedirectTo = "",
  accountSessionDraft,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isAccountLinkingOpen, setIsAccountLinkingOpen] = useState(false);
  const [isRecoveryOnly, setIsRecoveryOnly] = useState(false);
  const [sessionIntent, setSessionIntent] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [impact, setImpact] = useState(null);
  const [impactStateUid, setImpactStateUid] = useState(null);
  const [isImpactLoading, setIsImpactLoading] = useState(false);
  const [impactError, setImpactError] = useState("");
  const currentUidRef = useRef(user?.uid || null);
  const impactRequestIdRef = useRef(0);
  const recoveryPromptKeyRef = useRef("");
  const isAnonymousUser = user?.isAnonymous === true;
  const isSignedInAccount = Boolean(user && !isAnonymousUser);

  useEffect(() => {
    const nextUid = user?.uid || null;
    currentUidRef.current = nextUid;
    impactRequestIdRef.current += 1;
    setImpact(null);
    setImpactStateUid(null);
    setImpactError("");
    setIsImpactLoading(false);
  }, [user?.uid]);

  useEffect(() => {
    const recoveryState = getAccountSessionRecoveryState();
    const recoveryKey = recoveryState
      ? `${recoveryState.createdAt}:${recoveryState.destinationUid || "pending"}`
      : "";

    if (
      !hasUnfinishedAccountSessionRecovery(recoveryState) ||
      recoveryPromptKeyRef.current === recoveryKey
    ) {
      return;
    }

    if (user?.uid) {
      setIsAccountLinkingOpen(true);
      setIsRecoveryOnly(true);
      recoveryPromptKeyRef.current = recoveryKey;
    }
  }, [user?.uid]);

  const loadImpact = async () => {
    const requestUid = user?.uid || null;
    const requestId = impactRequestIdRef.current + 1;
    impactRequestIdRef.current = requestId;
    setImpactStateUid(null);
    setIsImpactLoading(true);
    setImpactError("");

    try {
      const nextImpact = await getYourImpact();

      if (
        currentUidRef.current === requestUid &&
        impactRequestIdRef.current === requestId
      ) {
        setImpact(nextImpact);
        setImpactStateUid(requestUid);
      }
    } catch (error) {
      if (
        currentUidRef.current === requestUid &&
        impactRequestIdRef.current === requestId
      ) {
        setImpactError("We couldn't load your impact just now.");
        setImpactStateUid(requestUid);
      }
    } finally {
      if (
        currentUidRef.current === requestUid &&
        impactRequestIdRef.current === requestId
      ) {
        setIsImpactLoading(false);
      }
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    if (isSignedInAccount) {
      loadImpact();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleSignOut = async () => {
    setStatusMessage("");
    const result = await signOutUser();

    if (result.status !== GOOGLE_AUTH_OUTCOMES.SUCCESS) {
      setStatusMessage("We couldn't sign you out just now. Please try again.");
      return;
    }

    impactRequestIdRef.current += 1;
    setImpact(null);
    setImpactStateUid(null);
    setImpactError("");
    setIsImpactLoading(false);
    setIsOpen(false);
    if (signOutRedirectTo) {
      navigate(signOutRedirectTo);
    }
  };

  const handleSignIn = async () => {
    setStatusMessage("");

    if (isAnonymousUser) {
      const savedIntent = saveAccountMenuIntent({
        sourceUid: user.uid,
        returnTo: `${location.pathname}${location.search}`,
        draft: accountSessionDraft,
      });

      if (!savedIntent) {
        setStatusMessage(
          "We couldn't save this session for recovery. Please try again."
        );
        return;
      }

      setSessionIntent(savedIntent);
      setIsOpen(false);
      setIsAccountLinkingOpen(true);
      setIsRecoveryOnly(false);
      return;
    }

    const result = await signInWithGoogle();

    if (result.status === GOOGLE_AUTH_OUTCOMES.SUCCESS) {
      setIsOpen(false);
      return;
    }

    if (result.status === GOOGLE_AUTH_OUTCOMES.CANCELLED) {
      return;
    }

    setStatusMessage("We couldn't sign you in just now. Please try again.");
  };

  const handleDismissLinking = () => {
    if (!isRecoveryOnly) {
      clearAccountMenuIntent();
    }
    setIsAccountLinkingOpen(false);
    setIsRecoveryOnly(false);
  };

  const handleLinked = () => {
    clearAccountMenuIntent();
    setIsOpen(false);
    setIsAccountLinkingOpen(false);
    setIsRecoveryOnly(false);
  };

  const displayName = getAccountDisplayName(user);
  const accountButtonLabel = user
    ? `Open account menu for ${displayName}`
    : "Open account menu";

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#f4d94e]/10 text-[#fff6b8] shadow-[0_0_26px_rgba(244,217,78,0.26),0_12px_28px_rgba(0,0,0,0.22),inset_0_1px_0_rgba(244,217,78,0.16)] backdrop-blur-xl transition hover:bg-[#f4d94e]/16 active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f4d94e] sm:h-[50px] sm:w-[50px]"
        aria-label={accountButtonLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span className="pointer-events-none absolute inset-0 rounded-full bg-[radial-gradient(circle_at_50%_0%,rgba(244,217,78,0.16),transparent_70%)]" />
        <AccountAvatar user={user} className="relative h-9 w-9 sm:h-10 sm:w-10" showBorder={false} />
      </button>

      {isOpen ? (
        <AccountMenu
          user={user}
          statusMessage={statusMessage}
          onDismiss={() => setIsOpen(false)}
          onSignIn={handleSignIn}
          onSignOut={handleSignOut}
          impact={
            impactStateUid === (user?.uid || null) ? impact : null
          }
          isImpactLoading={isImpactLoading}
          impactError={
            impactStateUid === (user?.uid || null) ? impactError : ""
          }
          onRetryImpact={loadImpact}
        />
      ) : null}

      <AccountLinkingSheet
        isOpen={isAccountLinkingOpen}
        intent="keep-cards"
        sessionSave={!isRecoveryOnly}
        sessionIntent={sessionIntent}
        recoveryOnly={isRecoveryOnly}
        onDismiss={handleDismissLinking}
        onLinked={handleLinked}
      />
    </>
  );
}

export default AccountAvatarMenu;
